import React, { useState, useRef, ChangeEvent, DragEvent } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styles from "./styles.module.scss";
import { UploadFile } from './File/File'
import ClientSideFile from './File/ClientSideFile'

interface Params {
  multiple?: boolean;
  onDelete?: (file: UploadFile) => unknown;
  onFilesAdded?: (files: File[]) => unknown;
  onValidationError?: (message: string) => void;
  initialFiles?: UploadFile[],
  accept: string|null,
  maxSizeInMB: number|null,
  formats?: string[]
}

export default function FileUploader(
  { onDelete, onFilesAdded, onValidationError, initialFiles, accept, multiple = true, formats, maxSizeInMB }: Params
) {
  const [files, setFiles] = useState<UploadFile[]>(initialFiles || []);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDisabled = files.length > 0 && !multiple
  const { t } = useTranslation();

  const translations = [
    maxSizeInMB && t('file_uploader.max_size', { sizeInMb: maxSizeInMB }),
    (formats && formats.length > 0) && t('file_uploader.formats', { formats: formats.map(f => f.trim()).join(', ') })
  ].filter(text => text);

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const fileArray = Array.from(newFiles);

    const validFiles: File[] = [];
    const rejectedNames: string[] = [];

    fileArray.forEach(file => {
      if (maxSizeInMB && file.size > maxSizeInMB * 1024 * 1024) {
        rejectedNames.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (rejectedNames.length > 0) {
      onValidationError?.(
        t('file_uploader.file_too_large', { names: rejectedNames.join(', '), maxSize: maxSizeInMB })
      );
    }

    if (validFiles.length === 0) return;

    setFiles((prev) => [...prev, ...validFiles.map((file: File) => ClientSideFile(file))]);
    onFilesAdded?.(validFiles);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (isDisabled) {
      return
    }
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = (index: number) => {
    const file = files.find((_, i) => i === index);

    if (file === undefined) {
      return;
    }

    onDelete?.(file);

    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div className={styles.main}>
      <div
        className={clsx(styles.uploader, !isDisabled && styles.uploaderNotDisabled)}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !isDisabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          onChange={handleChange}
          className={styles.hiddenInput}
          accept={accept || undefined}
        />
        {!isDisabled && <p className={styles.placeholder}>Drag and drop files here or click to select</p>}
        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((file, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className={styles.fileItem}>
                <div className={styles.fileInfo}>
                  <span className={styles.fileName}>{file.name()}</span>
                  <span className={styles.fileSize}>{file.size()} MB</span>
                </div>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {translations.length > 0 && <small>{translations.filter(text => text).join(', ')}</small>}
    </div>
  );
}
